import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import hotPropsService from '../../utils/services/hotPropsService';
import resultsTrackerService from '../../utils/services/resultsTrackerService';
import HotPropRow from './HotPropRow';
import HotPropsFiltersModal, { DEFAULT_HOT_PROPS_FILTERS } from './HotPropsFiltersModal';
import RawOddsTrackerModal from '../OddsScreen/RawOddsTrackerModal';
import Toast from '../Toast/Toast';
import { BOOKMAKER_LOGOS } from './bookmakerAssets';

const SPORT_OPTIONS = [
    { value: 'afl', label: 'AFL' },
];

const SORT_OPTIONS = [
    { value: 'edge', label: 'Most Edge' },
    { value: 'movement', label: 'Market Movers' },
];

const SORT_TO_FILTER_TYPE = {
    edge: 'most_edge',
    movement: 'market_movers',
};

const REFRESH_INTERVAL = 60;

// Column count across all screen sizes (used for colSpan on expanded rows).
// Desktop: Rank, Prop, Market, Movement, Mover, Best, Spark, Books, Track, Caret = 10
const TOTAL_COLS = 10;

function LiveDot() {
    return (
        <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-40" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
    );
}

function CountdownRing({ countdown, total }) {
    const progress = ((total - countdown) / total) * 100;
    const circumference = 2 * Math.PI * 11;
    const strokeOffset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative w-8 h-8 flex items-center justify-center">
            <svg className="w-8 h-8 -rotate-90" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1" className="text-slate-800" />
                <circle
                    cx="12" cy="12" r="11"
                    fill="none" stroke="currentColor" strokeWidth="1.5"
                    className="text-emerald-500 transition-all duration-1000 ease-linear"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeOffset}
                    strokeLinecap="round"
                />
            </svg>
            <span className="absolute text-[9px] font-mono font-medium text-emerald-500/80 tabular-nums">
                {countdown}
            </span>
        </div>
    );
}

function FlameIcon({ className = 'w-5 h-5' }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.982 1.566a1 1 0 00-1.586-.057C8.4 4.91 6.002 8.518 6.002 13a6 6 0 1012 0c0-2.72-1.54-5.024-3.155-6.817a1 1 0 00-1.627.226 5.506 5.506 0 01-1.42 1.787A9.24 9.24 0 0012.982 1.566zM13 18a4 4 0 01-4-4 3.98 3.98 0 01.886-2.513 7.5 7.5 0 001.345.934c.85.46 1.769.579 2.769.579 1 0 1.919-.119 2.769-.579A4 4 0 0113 18z" />
        </svg>
    );
}

function formatGameLabel(match) {
    if (!match) return 'Unknown';
    // Compact "Team A vs Team B" → "Team A v Team B" doesn't help much; show as-is.
    return match;
}

function GameSelect({ value, options, onChange, className = '', placeholder = 'All games' }) {
    const isAll = value === 'all';
    const selectedLabel = isAll ? placeholder : formatGameLabel(value);

    return (
        <div className={`relative ${className}`}>
            <span
                aria-hidden="true"
                className={`pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 ${
                    isAll ? 'text-slate-600' : 'text-emerald-400'
                }`}
            >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5l7 7-7 7"
                    />
                </svg>
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-label="Filter by game"
                title={selectedLabel}
                className={`appearance-none pl-7 pr-7 py-1.5 bg-slate-800 border rounded text-xs font-mono cursor-pointer hover:border-slate-600 focus:outline-none focus:border-emerald-500 transition-colors max-w-[180px] truncate ${
                    isAll
                        ? 'border-slate-700 text-slate-300'
                        : 'border-emerald-500/40 bg-emerald-500/5 text-emerald-200'
                }`}
            >
                <option value="all">All games</option>
                {options.map((m) => (
                    <option key={m} value={m}>
                        {formatGameLabel(m)}
                    </option>
                ))}
            </select>
            <svg
                aria-hidden="true"
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
            >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
        </div>
    );
}

function SortPill({ label, value, selected, onClick }) {
    return (
        <button
            onClick={() => onClick(value)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-all duration-150 ${
                selected
                    ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                    : 'text-slate-400 hover:text-slate-200 border border-transparent hover:border-slate-700'
            }`}
        >
            {label}
        </button>
    );
}

function HotProps() {
    const [sport, setSport] = useState('afl');
    const [propsData, setPropsData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('edge');
    const [filters, setFilters] = useState(DEFAULT_HOT_PROPS_FILTERS);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
    const [expandedKey, setExpandedKey] = useState(null);
    const [selectedGame, setSelectedGame] = useState('all');
    const [trackerEntry, setTrackerEntry] = useState(null);
    const [isAddingBet, setIsAddingBet] = useState(false);
    const [unitSize, setUnitSize] = useState(20);
    const [toast, setToast] = useState(null);

    const countdownRef = useRef(null);
    const refreshIntervalRef = useRef(null);

    const filterType = SORT_TO_FILTER_TYPE[sortBy] || 'market_movers';

    const fetchHotProps = useCallback(async (params, showLoading = false) => {
        if (showLoading) setIsLoading(true);
        setError(null);

        const result = await hotPropsService.getHotProps(params);

        if (result.success) {
            setPropsData(result.data);
        } else {
            setPropsData(null);
            setError(result);

            if (result.isSubscriptionError || result.isAuthError) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
            }
        }

        setIsLoading(false);
    }, []);

    // Sport change replaces the whole match set — reset the local game pick.
    useEffect(() => {
        setSelectedGame('all');
    }, [sport]);

    useEffect(() => {
        const activeMovementThreshold =
            filterType === 'market_movers'
                ? filters.movers_movement_threshold
                : filters.edge_movement_threshold;

        const requestParams = {
            sport,
            filter_type: filterType,
            movement_threshold: activeMovementThreshold,
            edge_threshold: filters.edge_threshold,
            movement_bookmaker: filters.movement_bookmaker,
            exclude_best_bookmakers: filters.exclude_best_bookmakers,
            minimum_odds: filters.minimum_odds,
        };

        fetchHotProps(requestParams, true);
        setCountdown(REFRESH_INTERVAL);

        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

        countdownRef.current = setInterval(() => {
            setCountdown((prev) => (prev <= 1 ? REFRESH_INTERVAL : prev - 1));
        }, 1000);

        refreshIntervalRef.current = setInterval(() => {
            fetchHotProps(requestParams);
            setCountdown(REFRESH_INTERVAL);
        }, REFRESH_INTERVAL * 1000);

        return () => {
            clearInterval(countdownRef.current);
            clearInterval(refreshIntervalRef.current);
        };
    }, [sport, filterType, filters, fetchHotProps]);

    // Pull the user's unit size for the tracker modal stake presets (mirrors OddsScreen).
    useEffect(() => {
        const fetchSettings = async () => {
            const result = await resultsTrackerService.getSettings();
            if (!result.success || !result.data) return;
            const { advancedUnitSettings, monthlyUnits, unitSize: defaultUnit } = result.data;
            if (advancedUnitSettings && monthlyUnits?.length > 0) {
                const now = new Date();
                const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                const match = monthlyUnits
                    .filter((m) => m.startDate?.substring(0, 7) <= currentMonth)
                    .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
                setUnitSize(match?.unitSize || defaultUnit || 20);
            } else {
                setUnitSize(defaultUnit || 20);
            }
        };
        fetchSettings();
    }, []);

    const showToast = useCallback((message, type = 'error', duration = 5000) => {
        setToast({ message, type, duration });
    }, []);

    // Build an OddsScreen-shaped entry from a hot prop so we can reuse the existing tracker modal.
    // The modal expects `entry.odds` keyed by bookmaker → current price, plus player/market/match/startTime.
    const openTracker = useCallback((prop) => {
        const odds = {};
        for (const ph of prop?.price_history || []) {
            if (ph?.bookmaker && ph.current_price != null) {
                odds[ph.bookmaker] = Number(ph.current_price);
            }
        }
        if (prop?.best_odds?.bookmaker && prop.best_odds.current_price != null) {
            odds[prop.best_odds.bookmaker] = Number(prop.best_odds.current_price);
        }
        setTrackerEntry({
            player: prop.player,
            market: prop.market,
            match: prop.match,
            startTime: prop.start_time || prop.startTime || null,
            odds,
        });
    }, []);

    const handleAddToTracker = useCallback(async (betPayload) => {
        setIsAddingBet(true);
        const result = await resultsTrackerService.addBet(betPayload);
        if (result.success) {
            showToast('Bet added to tracker successfully', 'success');
            setTrackerEntry(null);
        } else if (result.isAuthError) {
            showToast('Session expired. Please log in again.', 'error');
        } else {
            showToast(result.message || 'Failed to add bet to tracker', 'error');
        }
        setIsAddingBet(false);
        return result;
    }, [showToast]);

    const props = useMemo(() => propsData?.props || [], [propsData]);

    // Derive list of games present in the current payload (sorted, deduped).
    const availableGames = useMemo(() => {
        const set = new Set();
        for (const p of props) {
            if (p?.match) set.add(p.match);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [props]);

    // If the selected game disappears from the data (refresh, sport change, etc.),
    // fall back to "all" so we don't show an empty list silently.
    useEffect(() => {
        if (selectedGame !== 'all' && !availableGames.includes(selectedGame)) {
            setSelectedGame('all');
        }
    }, [selectedGame, availableGames]);

    // Backend already sorts by the active filter_type; apply client-side search + game filter.
    const sortedProps = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return props.filter((p) => {
            if (selectedGame !== 'all' && p.match !== selectedGame) return false;
            if (!term) return true;
            return (
                (p.player || '').toLowerCase().includes(term) ||
                (p.match || '').toLowerCase().includes(term) ||
                (p.market || '').toLowerCase().includes(term)
            );
        });
    }, [props, searchTerm, selectedGame]);

    // Derive bookmaker list dynamically from the current payload (docs recommend this).
    const availableBookmakers = useMemo(() => {
        const set = new Set();
        for (const p of props) {
            if (p?.best_odds?.bookmaker) set.add(p.best_odds.bookmaker);
            for (const ph of p?.price_history || []) {
                if (ph.bookmaker) set.add(ph.bookmaker);
            }
        }
        return Array.from(set);
    }, [props]);

    const activeFilterCount = useMemo(() => {
        let n = 0;
        if (filterType === 'market_movers') {
            if (
                filters.movers_movement_threshold !==
                DEFAULT_HOT_PROPS_FILTERS.movers_movement_threshold
            ) n++;
        } else {
            if (
                filters.edge_threshold !==
                DEFAULT_HOT_PROPS_FILTERS.edge_threshold
            ) n++;
            if (
                filters.edge_movement_threshold !==
                DEFAULT_HOT_PROPS_FILTERS.edge_movement_threshold
            ) n++;
            if (filters.movement_bookmaker) n++;
            if ((filters.exclude_best_bookmakers || []).length > 0) n++;
            if (filters.minimum_odds != null && filters.minimum_odds !== '') n++;
        }
        return n;
    }, [filterType, filters]);

    const toggleRow = useCallback((key) => {
        setExpandedKey((cur) => (cur === key ? null : key));
    }, []);

    const renderEmptyState = () => (
        <div className="flex flex-col items-center justify-center py-24 px-6">
            <div className="w-12 h-12 rounded-lg bg-slate-800/80 border border-slate-700/50 flex items-center justify-center mb-4 text-slate-600">
                <FlameIcon className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-slate-400 mb-1">No props match your search</p>
            <p className="text-xs text-slate-600 font-mono">
                {searchTerm ? 'Try a different search term' : 'No hot props available right now'}
            </p>
        </div>
    );

    const renderErrorState = () => {
        if (!error || isLoading) return null;

        if (error.isSubscriptionError) {
            return (
                <div className="flex items-center justify-center py-24 px-6">
                    <div className="flex flex-col items-center text-center max-w-sm">
                        <div className="w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-5 text-amber-400">
                            <FlameIcon className="w-7 h-7" />
                        </div>
                        <h4 className="text-base font-semibold font-mono text-white mb-2">Subscription Required</h4>
                        <p className="text-sm font-mono text-slate-500 leading-relaxed mb-6">
                            A Snipe Pro or Snipe Elite subscription is required to access Hot Props.
                        </p>
                        <a
                            href="/manage-subscriptions"
                            className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg text-sm font-semibold hover:from-amber-400 hover:to-orange-500 transition-all flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Manage Subscriptions
                        </a>
                    </div>
                </div>
            );
        }

        const errorConfig = {
            AUTH: {
                icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636',
                color: 'text-red-400', bg: 'bg-red-500/5 border-red-500/10', title: 'Authentication Failed',
            },
            RATE_LIMIT_EXCEEDED: {
                icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
                color: 'text-amber-400', bg: 'bg-amber-500/5 border-amber-500/10', title: 'Too Many Requests',
            },
            SERVICE_UNAVAILABLE: {
                icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z',
                color: 'text-slate-400', bg: 'bg-slate-500/5 border-slate-500/10', title: 'Service Unavailable',
            },
            NO_DATA: {
                icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z',
                color: 'text-slate-400', bg: 'bg-slate-500/5 border-slate-500/10', title: 'No Movement Yet',
            },
            NETWORK_ERROR: {
                icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                color: 'text-red-400', bg: 'bg-red-500/5 border-red-500/10', title: 'Connection Error',
            },
            VALIDATION_ERROR: {
                icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                color: 'text-amber-400', bg: 'bg-amber-500/5 border-amber-500/10', title: 'Invalid Request',
            },
        };

        const code = error.isAuthError ? 'AUTH' : (error.errorCode || 'NETWORK_ERROR');
        const config = errorConfig[code] || errorConfig.NETWORK_ERROR;
        const showRetry = ['SERVICE_UNAVAILABLE', 'NO_DATA', 'NETWORK_ERROR', 'SERVER_ERROR'].includes(error.errorCode);

        return (
            <div className="flex items-center justify-center py-24 px-6">
                <div className="flex flex-col items-center text-center max-w-sm">
                    <div className={`w-12 h-12 rounded-lg ${config.bg} border flex items-center justify-center mb-4`}>
                        <svg className={`w-6 h-6 ${config.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={config.icon} />
                        </svg>
                    </div>
                    <h4 className={`text-sm font-semibold mb-1.5 ${config.color}`}>{config.title}</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">{error.message}</p>
                    {showRetry && (
                        <button
                            onClick={() => {
                                fetchHotProps({
                                    sport,
                                    filter_type: filterType,
                                    movement_threshold:
                                        filterType === 'market_movers'
                                            ? filters.movers_movement_threshold
                                            : filters.edge_movement_threshold,
                                    edge_threshold: filters.edge_threshold,
                                    movement_bookmaker: filters.movement_bookmaker,
                                    exclude_best_bookmakers: filters.exclude_best_bookmakers,
                                    minimum_odds: filters.minimum_odds,
                                }, true);
                                setCountdown(REFRESH_INTERVAL);
                            }}
                            className="mt-5 px-4 py-2 text-xs font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all flex items-center gap-2"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Retry
                        </button>
                    )}
                    {error.errorCode === 'RATE_LIMIT_EXCEEDED' && (
                        <p className="mt-3 text-[10px] font-mono text-slate-700">Resets every 60s</p>
                    )}
                </div>
            </div>
        );
    };

    const totalCount = propsData?.count || 0;

    const renderTable = () => {
        if (isLoading && !propsData) return null;
        if (!propsData) return null;
        if (sortedProps.length === 0) return renderEmptyState();

        return (
            <>
                <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-800/90 backdrop-blur-md border-b border-slate-700/50">
                        <th className="text-center px-3 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap w-12">
                            #
                        </th>
                        <th className="text-left px-3 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap">
                            Prop
                        </th>
                        <th className="text-left px-3 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap hidden md:table-cell">
                            Market
                        </th>
                        <th className="text-center px-3 py-3 whitespace-nowrap">
                            <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest">
                                Movement
                            </span>
                        </th>
                        <th className="text-left px-3 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap hidden lg:table-cell">
                            Biggest Mover
                        </th>
                        <th className="text-left px-3 py-3 whitespace-nowrap">
                            <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest">
                                Best
                            </span>
                        </th>
                        <th className="text-left px-3 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap hidden md:table-cell">
                            Flucts
                        </th>
                        <th className="text-center px-3 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap hidden lg:table-cell">
                            Books
                        </th>
                        <th className="text-center px-2 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap">
                            Track
                        </th>
                        <th className="px-3 py-3 w-8" aria-label="Expand" />
                    </tr>
                </thead>
                <tbody>
                    {sortedProps.map((prop, i) => {
                        const key = `${prop.match}|${prop.player}|${prop.market}`;
                        return (
                            <HotPropRow
                                key={key}
                                prop={prop}
                                rank={i + 1}
                                isExpanded={expandedKey === key}
                                onToggle={() => toggleRow(key)}
                                onTrack={openTracker}
                                colCount={TOTAL_COLS}
                            />
                        );
                    })}
                </tbody>
            </>
        );
    };

    return (
        <>
        <div className="flex flex-col p-2 md:p-4 h-[calc(100vh-72px)] overflow-hidden">
            <div className="bg-slate-900/50 rounded-xl md:rounded-2xl border border-slate-700/50 overflow-hidden relative flex flex-col flex-1 min-h-0">
                {/* Initial loading overlay */}
                {isLoading && !propsData && (
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm z-10 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-3">
                            <div className="flex gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '0ms' }} />
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '200ms' }} />
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '400ms' }} />
                            </div>
                            <span className="text-xs font-mono text-slate-500">Scanning market movements</span>
                        </div>
                    </div>
                )}

                {/* Refresh loading overlay */}
                {isLoading && propsData && (
                    <div className="absolute inset-0 bg-slate-900/40 z-10 flex items-center justify-center pointer-events-none">
                        <div className="flex items-center gap-2.5 bg-slate-800 px-4 py-2.5 rounded-lg border border-slate-700/50 shadow-xl pointer-events-auto">
                            <div className="w-4 h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                            <span className="text-xs font-mono text-slate-400">Updating...</span>
                        </div>
                    </div>
                )}

                {/* Command Bar Header */}
                <div className="border-b border-slate-700/50">
                    {/* Desktop Header */}
                    <div className="hidden md:flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2.5">
                                <FlameIcon className="w-4 h-4 text-amber-400" />
                                <h3 className="text-sm font-semibold text-white tracking-tight">Hot Props</h3>
                                {!isLoading && propsData && (
                                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-800/50 rounded">
                                        <span className="text-[11px] font-mono font-semibold text-white">{totalCount}</span>
                                        <span className="text-[11px] font-mono text-slate-600">props</span>
                                    </div>
                                )}
                            </div>

                            <div className="w-px h-5 bg-slate-700/50" />

                            <select
                                value={sport}
                                onChange={(e) => setSport(e.target.value)}
                                className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                            >
                                {SPORT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>

                            <div className="flex items-center gap-1">
                                {SORT_OPTIONS.map(opt => (
                                    <SortPill
                                        key={opt.value}
                                        label={opt.label}
                                        value={opt.value}
                                        selected={sortBy === opt.value}
                                        onClick={setSortBy}
                                    />
                                ))}
                            </div>

                            <div className="w-px h-5 bg-slate-700/50" />

                            <button
                                type="button"
                                onClick={() => setFiltersOpen(true)}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
                                    activeFilterCount > 0
                                        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                        : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
                                }`}
                                aria-label="Open filters"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                                </svg>
                                <span>Filters</span>
                                {activeFilterCount > 0 && (
                                    <span className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-400 text-emerald-950 text-[9px] font-mono font-bold leading-none inline-flex items-center justify-center">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>

                            <GameSelect
                                value={selectedGame}
                                options={availableGames}
                                onChange={setSelectedGame}
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search player, market, match..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-8 pr-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-56 hover:border-slate-600 transition-colors"
                                />
                            </div>

                            {!isLoading && !error && (
                                <div className="flex items-center gap-2">
                                    <LiveDot />
                                    <CountdownRing countdown={countdown} total={REFRESH_INTERVAL} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile Header */}
                    <div className="flex md:hidden items-center justify-between px-3 py-2.5">
                        <div className="flex items-center gap-2">
                            <FlameIcon className="w-4 h-4 text-amber-400" />
                            <h3 className="text-sm font-semibold text-white">Hot Props</h3>
                            {!isLoading && propsData && (
                                <span className="text-[11px] font-mono text-slate-600">
                                    <span className="text-slate-400 font-semibold">{totalCount}</span>
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {!isLoading && !error && (
                                <CountdownRing countdown={countdown} total={REFRESH_INTERVAL} />
                            )}
                            <button
                                type="button"
                                onClick={() => setFiltersOpen(true)}
                                className={`relative p-1.5 border rounded transition-colors ${
                                    activeFilterCount > 0
                                        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                        : 'border-slate-700 text-slate-500 hover:text-white hover:border-slate-600'
                                }`}
                                aria-label="Open filters"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
                                </svg>
                                {activeFilterCount > 0 && (
                                    <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-1 rounded-full bg-emerald-400 text-emerald-950 text-[9px] font-mono font-bold leading-none inline-flex items-center justify-center">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setMobileSettingsOpen((prev) => !prev)}
                                className="p-1.5 border border-slate-700 rounded text-slate-500 hover:text-white hover:border-slate-600 transition-colors"
                                aria-label="Toggle sport and sort"
                            >
                                <svg className={`w-4 h-4 transition-transform duration-200 ${mobileSettingsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {mobileSettingsOpen && (
                        <div className="md:hidden px-3 pb-3 border-t border-slate-700/50 pt-3 animate-fade-in space-y-3">
                            <div className="flex gap-2">
                                <select
                                    value={sport}
                                    onChange={(e) => setSport(e.target.value)}
                                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                >
                                    {SPORT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                >
                                    {SORT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <select
                                value={selectedGame}
                                onChange={(e) => setSelectedGame(e.target.value)}
                                aria-label="Filter by game"
                                className={`w-full px-3 py-2 border rounded text-xs font-mono focus:outline-none focus:border-emerald-500 ${
                                    selectedGame === 'all'
                                        ? 'bg-slate-800 border-slate-700 text-white'
                                        : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
                                }`}
                            >
                                <option value="all">All games</option>
                                {availableGames.map((m) => (
                                    <option key={m} value={m}>
                                        {formatGameLabel(m)}
                                    </option>
                                ))}
                            </select>
                            <div className="relative">
                                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-full"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Error state */}
                {error && renderErrorState()}

                {/* Table */}
                {!error && (
                    <div className="overflow-auto flex-1 min-h-0 styled-scrollbar">
                        <table className="w-full table-auto">
                            {renderTable()}
                        </table>
                    </div>
                )}

                {/* Status footer */}
                {!error && !isLoading && propsData && (
                    <div className="px-4 py-2.5 border-t border-slate-700/50 flex items-center justify-between">
                        <div className="text-[11px] font-mono text-slate-600">
                            <span className="text-slate-500">{sortedProps.length}</span>
                            {sortedProps.length !== totalCount && (
                                <span className="text-slate-700"> / {totalCount}</span>
                            )}
                            <span className="ml-1">hot props</span>
                        </div>
                        <div className="text-[10px] font-mono text-slate-700 flex items-center gap-1.5">
                            <span>Sorted by</span>
                            <span className="text-emerald-500/80">
                                {SORT_OPTIONS.find((s) => s.value === sortBy)?.label}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
        <HotPropsFiltersModal
            open={filtersOpen}
            filterType={filterType}
            filters={filters}
            bookmakers={availableBookmakers}
            onModeChange={(nextFilterType) => {
                const nextSort =
                    nextFilterType === 'most_edge' ? 'edge' : 'movement';
                setSortBy(nextSort);
            }}
            onApply={(next) => setFilters(next)}
            onClose={() => setFiltersOpen(false)}
        />

        {trackerEntry && (
            <RawOddsTrackerModal
                entry={trackerEntry}
                onClose={() => setTrackerEntry(null)}
                bookmakerLogos={BOOKMAKER_LOGOS}
                unitSize={unitSize}
                onAddToTracker={handleAddToTracker}
                isAddingBet={isAddingBet}
            />
        )}

        {toast && (
            <Toast
                message={toast.message}
                type={toast.type}
                duration={toast.duration}
                onClose={() => setToast(null)}
            />
        )}
        </>
    );
}

export default HotProps;
